import { Button, Divider, Flex, HStack, PinInput, PinInputField, Text } from '@chakra-ui/react'
import { useLoginMutation, useLoginViaSMSMutation } from '@core/app/api/authApi'
import { RECAPTCHA } from '@core/utils/constants/constants'
import { removeLocalStorageItem } from '@core/utils/helpers/localStorageHelpers'
import { useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useTranslation } from 'react-i18next'

interface Props {
    phone: string
    authData:
        | {
              email: string
              password: string
          }
        | undefined
    onError: (value: boolean) => void
}

export const PhoneVerificationStep = ({ phone, authData, onError }: Props) => {
    const [loginViaSms] = useLoginViaSMSMutation()
    const [login] = useLoginMutation()
    const [count, setCount] = useState<number>(90)
    const [err, setErr] = useState<string>('')

    const recaptchaRef = useRef<ReCAPTCHA>(null)
    const lastPinInput = useRef<HTMLInputElement>(null)

    const { t } = useTranslation()

    const loginHandler = async (code: string) => {
        recaptchaRef.current?.reset()
        let token = await recaptchaRef.current?.executeAsync()
        if (token) {
            try {
                const response = await loginViaSms({
                    data: {
                        code,
                        'g-recaptcha-response': token,
                    },
                }).unwrap()

                location.href = response.redirectUrl
            } catch (error: any) {
                const errorMessage = error?.data?.message || 'An error occurred'
                setErr(errorMessage)

                setTimeout(() => {
                    onError(true)
                }, 1500)
            } finally {
                removeLocalStorageItem('feedbackTop')
            }
        }
    }

    const resendCode = async () => {
        recaptchaRef.current?.reset()
        let token = await recaptchaRef.current?.executeAsync()
        if (authData && token) {
            try {
                await login({
                    data: {
                        email: authData.email,
                        password: authData.password,
                        'g-recaptcha-response': token,
                    },
                }).unwrap()
                setCount(90)
            } catch (error) {
                console.log(error)
            }
        }
    }

    useEffect(() => {
        if (count === 0) return

        let interval = setInterval(() => {
            setCount((prev) => prev - 1)
        }, 1000)
        return () => clearInterval(interval)
    }, [count])

    return (
        <Flex flexDirection={'column'}>
            <Text fontSize={'2xl'} color={'text.main'} fontWeight={'semibold'} mb={4}>
                {t('6481')}
            </Text>
            <Text color={'text.secondary'}>{t('6588')}</Text>
            <Text variant={'noMargins'} color={'text.link'} fontWeight={'bold'}>
                {phone}
            </Text>
            <HStack mt={10}>
                <PinInput
                    otp
                    placeholder={''}
                    size={'lg'}
                    autoFocus={true}
                    focusBorderColor={'state.primary'}
                    type={'alphanumeric'}
                    onComplete={(value) => {
                        lastPinInput.current?.blur()
                        loginHandler(value)
                    }}
                    isInvalid={err.length > 0}
                    onChange={() => setErr('')}
                    errorBorderColor={'formElements.borderError'}
                >
                    <PinInputField w={'51px'} h={'60px'} />
                    <PinInputField w={'51px'} h={'60px'} />
                    <PinInputField w={'51px'} h={'60px'} />
                    <PinInputField ref={lastPinInput} w={'51px'} h={'60px'} />
                </PinInput>
            </HStack>
            {err && (
                <Text fontSize={'xs'} color={'text.error'} mt={1}>
                    {err}
                </Text>
            )}
            <Text fontSize={'sm'} color={'text.thirdly'} mt={2}>
                {t('6589')}{' '}
                <Text as="b" color={'text.secondary'}>
                    {count}
                </Text>{' '}
                {t('6590')}
            </Text>
            <Divider mt={'112px'} />
            <Text fontSize={'sm'} fontWeight={'medium'} color={'text.thirdly'} mt={6}>
                {t('6591')}.
            </Text>
            {count === 0 ? (
                <Button variant={'link'} color={'text.link'} mt={3} alignSelf={'start'} onClick={() => resendCode()}>
                    {t('6592')}
                </Button>
            ) : null}
            <ReCAPTCHA
                sitekey={RECAPTCHA}
                ref={recaptchaRef}
                size={'invisible'}
                onExpired={() => {
                    recaptchaRef.current?.reset()
                }}
            />
        </Flex>
    )
}
